import { FileType } from '@/types/files';

export enum MimeType {
	image = 'image/*',
	video = 'video/*',
	audio = 'audio/*',
	jpeg = 'image/jpeg',
	jpg = 'image/jpg',
	gif = 'image/gif',
	png = 'image/png',
	webp = 'image/webp',
	webm = 'video/webm',
	avi = 'video/avi',
	mpeg4 = 'video/mpeg4',
	wav = 'video/wav',
	mp4 = 'video/mp4',
	pdf = 'application/pdf',
	octetStream = 'application/octet-stream',
	doc = 'application/msword',
	docx = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	odt = 'application/vnd.oasis.opendocument.text',
}

export const mimeTypeMap: {
	type: FileType;
	extension: string;
	mimeType: MimeType;
}[] = [
	{ type: FileType.IMAGE, extension: '.jpg', mimeType: MimeType.jpg },
	{ type: FileType.IMAGE, extension: '.jpg', mimeType: MimeType.jpeg },
	{ type: FileType.IMAGE, extension: '.gif', mimeType: MimeType.gif },
	{ type: FileType.IMAGE, extension: '.png', mimeType: MimeType.png },
	{ type: FileType.IMAGE, extension: '.webp', mimeType: MimeType.webp },
	{ type: FileType.VIDEO, extension: '.webm', mimeType: MimeType.webm },
	{ type: FileType.VIDEO, extension: '.avi', mimeType: MimeType.avi },
	{ type: FileType.VIDEO, extension: '.mpeg4', mimeType: MimeType.mpeg4 },
	{ type: FileType.VIDEO, extension: '.wav', mimeType: MimeType.wav },
	{ type: FileType.VIDEO, extension: '.mp4', mimeType: MimeType.mp4 },
	{ type: FileType.PDF, extension: '.pdf', mimeType: MimeType.pdf },
	{ type: FileType.PDF, extension: '.pdf', mimeType: MimeType.octetStream },
	{ type: FileType.DOCUMENT, extension: '.doc', mimeType: MimeType.doc },
	{ type: FileType.DOCUMENT, extension: '.docx', mimeType: MimeType.docx },
	{ type: FileType.DOCUMENT, extension: '.odt', mimeType: MimeType.odt },
];

export const Mo = 1000 * 1000;
export const DEFAULT_MAX_MO = 10;
export const DEFAULT_MAX_SIZE = Mo * DEFAULT_MAX_MO;
export const DEFAULT_TOTAL_FILES_MAX_SIZE = Mo * 20;

export const DOCUMENT_EXTENSIONS_ALLOWED = ['.pdf', '.jpg', '.jpeg', '.png'];

interface FileSignature {
	bytes: number[];
	mimeTypes: MimeType[];
}

const FILE_SIGNATURES: FileSignature[] = [
	{
		bytes: [0x25, 0x50, 0x44, 0x46],
		mimeTypes: [MimeType.pdf, MimeType.octetStream],
	},
	{ bytes: [0xff, 0xd8, 0xff], mimeTypes: [MimeType.jpeg, MimeType.jpg] },
	{
		bytes: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a],
		mimeTypes: [MimeType.png],
	},
	{ bytes: [0x47, 0x49, 0x46, 0x38], mimeTypes: [MimeType.gif] },
	{ bytes: [0x52, 0x49, 0x46, 0x46], mimeTypes: [MimeType.webp] },
	{ bytes: [0xd0, 0xcf, 0x11, 0xe0], mimeTypes: [MimeType.doc] },
	{ bytes: [0x50, 0x4b, 0x03, 0x04], mimeTypes: [MimeType.docx, MimeType.odt] },
];

export const mimeTypeToFileType = (mimeType: string) =>
	mimeTypeMap.find((mime) => mime.mimeType === mimeType)?.type;

export const mimeTypeToExtension = (mimeType: string) =>
	mimeTypeMap.find((mime) => mime.mimeType === mimeType)?.extension;

export const getFileExtention = (file: string) => {
	if (!file) return '';
	if (file.startsWith('data:')) {
		return file.split(';')[0].split('/').pop() ?? '';
	}
	return file.split('.').pop()?.split('?')?.[0] ?? '';
};

export const isFileExtentions = (file: string, extentions: string[]) => {
	if (!file) return false;
	const extension = getFileExtention(file);
	if (!extension) return false;
	return extentions.some(
		(ext) => ext.toLowerCase() === extension.toLowerCase()
	);
};

export const cvExtensionIsPDFOrImage = (
	filePath: string | null | undefined
): boolean => {
	if (!filePath) return false;
	return isFileExtentions(filePath, ['pdf', 'jpg', 'jpeg', 'png', 'webp']);
};

export const isPdfSource = (source: string): boolean =>
	isFileExtentions(source, ['pdf']) || source.includes(MimeType.pdf);

export const isImageSource = (source: string): boolean =>
	isFileExtentions(source, ['jpg', 'jpeg', 'png', 'webp', 'gif']) ||
	[MimeType.jpeg, MimeType.jpg, MimeType.png, MimeType.webp, MimeType.gif].some(
		(mimeType) => source.includes(mimeType)
	) ||
	source.includes(MimeType.image.replace('*', ''));

export const isWordDocumentSource = (
	source: string | null | undefined
): boolean => {
	if (!source) return false;
	if (isFileExtentions(source, ['doc', 'docx'])) return true;
	return source.includes(MimeType.doc) || source.includes(MimeType.docx);
};

export const getFilenameFromPath = (
	path: string | null | undefined
): string => {
	if (!path) return '';
	const last = path.split('/').pop();
	if (!last) return '';
	return last.split('?')[0];
};

export const toReadableSize = (size: number) => {
	const baseOptions = {
		minimumFractionDigits: 0,
		maximumFractionDigits: 2,
		style: 'unit',
	} as Intl.NumberFormatOptions;
	if (size < 1e6)
		return (size / 1000).toLocaleString('fr-FR', {
			...baseOptions,
			unit: 'kilobyte',
		});
	return (size / 1000 / 1000).toLocaleString('fr-FR', {
		...baseOptions,
		unit: 'megabyte',
	});
};

export const fileInput = ({ accept = MimeType.image, multiple = false }) =>
	new Promise<File[] | null>((resolve) => {
		const input = document.createElement('input');
		input.type = 'file';
		input.accept = accept;
		input.multiple = multiple;
		const handleError = () => resolve(null);
		input.onchange = (event: Event) => {
			if (!event.target) return handleError();
			const files = (<HTMLInputElement>event.target)?.files;
			if (!files) return handleError();
			resolve(Array.from(files));
		};
		input.oncancel = () => handleError();
		input.onabort = () => handleError();
		input.click();
	});

export const fileToBase64 = (file: Blob) =>
	new Promise<string | null>((resolve) => {
		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onload = () => resolve(reader.result?.toString() ?? null);
		reader.onerror = () => resolve(null);
		reader.onabort = () => resolve(null);
	});

const readFileHeader = (file: File, size = 8): Promise<Uint8Array> =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
		reader.onerror = () => reject(reader.error);
		reader.readAsArrayBuffer(file.slice(0, size));
	});

/**
 * Reads the file's magic bytes and checks them against known signatures.
 * Returns true only if the actual binary content matches an accepted MIME type.
 * This catches renamed executables and other spoofed file extensions.
 */
export const validateFileSignature = async (
	file: File,
	acceptedMimeTypes: MimeType[]
): Promise<boolean> => {
	if (acceptedMimeTypes.length === 0) return true;

	try {
		const header = await readFileHeader(file);
		return matchSignature(header, acceptedMimeTypes);
	} catch {
		return false;
	}
};

/**
 * Same as validateFileSignature but works on a base64 data URL (data:...;base64,...).
 * Useful when the raw File object is no longer available.
 */
export const validateBase64Signature = (
	dataUrl: string,
	acceptedMimeTypes: MimeType[]
): boolean => {
	if (acceptedMimeTypes.length === 0) return true;

	try {
		const base64Content = dataUrl.split(',')[1];
		if (!base64Content) return false;

		const binaryStr = atob(base64Content.slice(0, 24));
		const header = new Uint8Array(binaryStr.length);
		for (let i = 0; i < binaryStr.length; i++) {
			header[i] = binaryStr.charCodeAt(i);
		}

		return matchSignature(header, acceptedMimeTypes);
	} catch {
		return false;
	}
};

const matchSignature = (
	header: Uint8Array,
	acceptedMimeTypes: MimeType[]
): boolean => {
	for (const signature of FILE_SIGNATURES) {
		const isMatch = signature.bytes.every((byte, i) => header[i] === byte);
		if (isMatch) {
			return signature.mimeTypes.some((mt) => acceptedMimeTypes.includes(mt));
		}
	}
	return false;
};

export const downloadLink = (link: string, definedFileName?: string) => {
	let fileName = definedFileName ?? link.split('/').pop() ?? 'file';

	if (!fileName.includes('.')) {
		const extension = getFileExtention(link);
		fileName = extension ? `${fileName}.${extension}` : `${fileName}.bin`;
	}

	const anchor = document.createElement('a');
	anchor.href = link;
	anchor.target = '_blank';
	anchor.download = fileName;
	anchor.click();
};
